import type { Device } from "@/api/types";

export interface DeviceGroup {
  label: string;
  deviceIds: string[];
  installs: Device[];
  primary: Device;
  manufacturer: string;
  model: string;
  androidVersion: string;
  latestAppVersion: string;
  firstSeen: string;
  lastSeen: string;
}

function hardwareKey(d: Device): string {
  // Prefer the server-computed logical_device_id when present — it's the canonical
  // hash for "same physical phone" and the source of truth across reinstalls.
  // Fall back to the same fingerprint shape when the field is missing
  // (during rollout or for records that pre-date the backfill).
  if (d.logical_device_id) return d.logical_device_id;
  const manufacturer = (d.manufacturer ?? "").trim().toLowerCase();
  const model = (d.model ?? "").trim().toLowerCase();
  return `${d.user_id}|${manufacturer}|${model}`;
}

function newest<T>(items: T[], key: (t: T) => string): T | undefined {
  return [...items].sort((a, b) => (key(b) ?? "").localeCompare(key(a) ?? ""))[0];
}

function oldest<T>(items: T[], key: (t: T) => string): T | undefined {
  return [...items].sort((a, b) => (key(a) ?? "").localeCompare(key(b) ?? ""))[0];
}

/**
 * Groups devices by user + manufacturer + model so a phone that was
 * uninstalled/reinstalled (changing device_id) collapses into one logical device.
 * Groups are labelled per-user in first_seen order.
 */
export function groupDevicesByHardware(devices: Device[]): DeviceGroup[] {
  const byUser: Record<string, Map<string, Device[]>> = {};
  for (const d of devices) {
    if (!byUser[d.user_id]) byUser[d.user_id] = new Map();
    const key = hardwareKey(d);
    const existing = byUser[d.user_id].get(key);
    if (existing) existing.push(d);
    else byUser[d.user_id].set(key, [d]);
  }

  const result: DeviceGroup[] = [];
  for (const userMap of Object.values(byUser)) {
    const groups = Array.from(userMap.values()).map<DeviceGroup>((installs) => {
      const sortedByFirst = [...installs].sort((a, b) =>
        (a.first_seen ?? "").localeCompare(b.first_seen ?? ""),
      );
      const primary = newest(installs, (d) => d.last_seen ?? "") ?? sortedByFirst[0];
      return {
        label: "",
        deviceIds: sortedByFirst.map((d) => d.device_id),
        installs: sortedByFirst,
        primary,
        manufacturer: primary.manufacturer ?? "",
        model: primary.model ?? "",
        androidVersion: primary.android_version ?? "",
        latestAppVersion: primary.app_version ?? "",
        firstSeen: oldest(installs, (d) => d.first_seen ?? "")?.first_seen ?? "",
        lastSeen: newest(installs, (d) => d.last_seen ?? "")?.last_seen ?? "",
      };
    });
    groups.sort((a, b) => a.firstSeen.localeCompare(b.firstSeen));
    groups.forEach((g, i) => {
      g.label = `Device ${i + 1}`;
    });
    result.push(...groups);
  }
  return result;
}

export function buildDeviceLabelMap(devices: Device[]): Record<string, string> {
  const groups = groupDevicesByHardware(devices);
  const map: Record<string, string> = {};
  for (const g of groups) {
    for (const id of g.deviceIds) map[id] = g.label;
  }
  return map;
}

/**
 * Fallback when the devices list isn't available (e.g. cross-participant data
 * explorer). When sensor records carry `logical_device_id` (post-backfill), all
 * physical-equal device_ids collapse to one label per participant. Otherwise
 * falls back to first-appearance order of `device_id` per participant.
 */
export function buildDeviceLabelMapFromRecords(
  records: { participant_id: string; device_id?: string; logical_device_id?: string }[],
): Record<string, string> {
  const userGroupOrder: Record<string, string[]> = {};
  const deviceIdsByGroup: Record<string, Set<string>> = {};

  for (const r of records) {
    if (!r.device_id) continue;
    const groupKey = r.logical_device_id || r.device_id;
    const userBucket = userGroupOrder[r.participant_id] ?? [];
    if (!userBucket.includes(groupKey)) {
      userBucket.push(groupKey);
      userGroupOrder[r.participant_id] = userBucket;
    }
    const set = deviceIdsByGroup[groupKey] ?? new Set();
    set.add(r.device_id);
    deviceIdsByGroup[groupKey] = set;
  }

  const map: Record<string, string> = {};
  for (const groupKeys of Object.values(userGroupOrder)) {
    groupKeys.forEach((groupKey, i) => {
      const label = `Device ${i + 1}`;
      const ids = deviceIdsByGroup[groupKey];
      if (ids) for (const id of ids) map[id] = label;
    });
  }
  return map;
}
